import { FC, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiPlaceholderArea } from "@miplatform/components";
import {
  NONE,
  ORDER_STATUS_DISPLAY,
  SECTION_TITLE_DATE_FORMAT,
} from "@miplatform/constants";
import { useDebounce } from "@miplatform/hooks";
import { IOrder } from "@miplatform/types";
import { formatDate, getDate } from "@miplatform/utils";
import { SidebarLayout } from "@mibuildweb/components";
import { Box } from "@mui/material";
import { OrdersAutocompleteSearch } from "./components/OrdersAutocompleteSearch";
import { OrdersFilterSidebar } from "./components/OrdersFilterSidebar";
import { OrdersList } from "../OrdersList/OrdersList";
import { ORDERS_TABS } from "../ordersConstants";
import {
  OrderSearchFilters,
  OrdersSearchApiRequest,
} from "./OrderSearch.types";
import { ORDERS_SEARCH_GROUP_AND_SORT_BY_KEYS } from "./OrdersSearch.constants";

export const OrdersSearch: FC<OrdersSearchProps> = ({
  search,
  orders,
  setOrders,
  getOrders,
  isFetchingOrders,
  refresh,
  onRefresh,
  orderCardConfig,
  orderHoverCardConfig,
  refreshPagination,
  orderReasons,
  setSearch,
  filters,
  initialFilters,
  setFilters,
}) => {
  const [isFiltered, setIsFiltered] = useState(false);

  const { classes, theme } = useStyles();
  const [autocompleteSearch, setAutocompleteSearch] = useState("");
  const debouncedOrderSearch = useDebounce(autocompleteSearch, 300);

  const showList = search || isFiltered;

  const toggleFiltered = (filtered: boolean) => {
    setIsFiltered(filtered);
  };

  const onFilterSearchPress = (appliedFilters: typeof filters) => {
    setFilters(appliedFilters);
    toggleFiltered(true);

    if (autocompleteSearch.trim().length > 0) {
      setSearch(autocompleteSearch.trim());
      setAutocompleteSearch("");
    }
  };

  const onResetFilters = () => {
    setFilters(initialFilters);
    toggleFiltered(false);
    setSearch("");
    setAutocompleteSearch("");
  };

  const onEnterPress = () => {
    if (autocompleteSearch.trim().length > 0) {
      setSearch(autocompleteSearch.trim());
      toggleFiltered(true);
    }
  };

  const getGroupedCriterias =
    filters.groupBy !== NONE
      ? (order: IOrder) => {
          switch (filters.groupBy) {
            case ORDERS_SEARCH_GROUP_AND_SORT_BY_KEYS.REQUESTER:
              if (!order.requester) {
                return "No Requester";
              }

              return (
                order.requester?.displayName ||
                `${order.requester?.firstName} ${order.requester?.lastName}`
              );
            case ORDERS_SEARCH_GROUP_AND_SORT_BY_KEYS.ORDER_STATUS:
              return ORDER_STATUS_DISPLAY[order.orderStatus];
            case ORDERS_SEARCH_GROUP_AND_SORT_BY_KEYS.ORDER_TYPE:
              return order.orderTemplateName;
            case ORDERS_SEARCH_GROUP_AND_SORT_BY_KEYS.BUILDER:
              return order.home?.builderName || "No Builder";
            case ORDERS_SEARCH_GROUP_AND_SORT_BY_KEYS.COMMUNITY:
              return order.home?.communityName || "No Community";
            case ORDERS_SEARCH_GROUP_AND_SORT_BY_KEYS.APPROVED_DATE:
              return order.approvedStartDate
                ? `Approved to start on: ${formatDate(
                    getDate(order.approvedStartDate),
                    SECTION_TITLE_DATE_FORMAT
                  )}`
                : "No Approved Start Date";
            case ORDERS_SEARCH_GROUP_AND_SORT_BY_KEYS.RECEIVED_DATE:
              return order.receivedDate
                ? `Received on: ${formatDate(
                    getDate(order.receivedDate),
                    SECTION_TITLE_DATE_FORMAT
                  )}`
                : "No Received Date";
            case ORDERS_SEARCH_GROUP_AND_SORT_BY_KEYS.CONFIRMATION_DATE:
              return order.confirmationDate
                ? `Confirmed on: ${formatDate(
                    getDate(order.confirmationDate),
                    SECTION_TITLE_DATE_FORMAT
                  )}`
                : "No Confirmation Date";
            default:
              return "";
          }
        }
      : undefined;

  const allTab = ORDERS_TABS[0];

  return (
    <SidebarLayout
      sidebarContent={
        <OrdersFilterSidebar
          filters={filters}
          initialFilters={initialFilters}
          onFilterSearchPress={onFilterSearchPress}
          onResetFilters={onResetFilters}
        />
      }
    >
      {!showList && (
        <Box className={classes.searchContainerDefault}>
          <MiPlaceholderArea
            showLogo
            helperText="Search for an order"
            containerStyle={{
              backgroundColor: theme.palette.common.white,
              height: "auto",
            }}
            logoStyle={{ height: 64 }}
          />
          <OrdersAutocompleteSearch
            search={autocompleteSearch}
            onSearchChange={setAutocompleteSearch}
            debouncedSearch={debouncedOrderSearch}
            onEnterPress={onEnterPress}
          />
        </Box>
      )}
      {showList && (
        <OrdersList
          key={JSON.stringify({ search, filters })}
          search={search}
          activeTab={allTab}
          orderItems={orders}
          isLoading={isFetchingOrders}
          setOrderItems={setOrders}
          getOrderItems={getOrders}
          refresh={refresh}
          onRefresh={onRefresh}
          orderCardConfig={orderCardConfig}
          orderHoverCardConfig={orderHoverCardConfig}
          refreshPagination={refreshPagination}
          orderReasons={orderReasons}
          groupedMode={!!(filters.groupBy && filters.groupBy !== NONE)}
          groupedCriteria={getGroupedCriterias}
        />
      )}
    </SidebarLayout>
  );
};

const useStyles = makeStyles()((theme) => ({
  searchContainerDefault: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    alignItems: "center",
    marginTop: theme.spacing(16),
  },
}));

interface OrdersSearchProps {
  search: string;
  setSearch: (search: string) => void;
  orders: IOrder[];
  setOrders: (orders: IOrder[]) => void;
  getOrders: (params: OrdersSearchApiRequest) => Promise<void>;
  isFetchingOrders: boolean;
  refresh: boolean;
  onRefresh: () => void;
  orderCardConfig: object[];
  orderHoverCardConfig: object[];
  refreshPagination: boolean;
  orderReasons: object[];
  filters: OrderSearchFilters;
  initialFilters: OrderSearchFilters;
  setFilters: (filters: OrderSearchFilters) => void;
}
